import { useState, useEffect } from "react";
import api from "../../utils/api";
import { uploadImageFile, getImageUrl } from "../../utils/imageUtils";
import { INP, LBL, S, THEME, TblRow, TblCell, actBtn, tblHead, ImageUploadField, useIsMobile } from "./AdminComponents";
import { useLanguage } from "../../context/LanguageContext";

const { G, G3, O, O2 } = THEME;

// Sectors are fetched dynamically from the Sectors admin API (same pattern
// as Events and News) — adding a sector on the Sectors page shows up here
// automatically.

const EMPTY_FORM = {
  name: "", address: "", sector: "", education: "",
  yearOfExcellence: "", additionalInfo: "", photo: "",
};

const CONTENT = {
  ne: {
    statTotal:         "कुल विशेषज्ञ",
    statSectors:       "क्षेत्रहरू ओगटिएको",
    deltaSoFar:        "अहिलेसम्म",
    deltaCovered:      "समेटिएको",
    searchPlaceholder: "🔍 नाम वा शिक्षाद्वारा खोज्नुहोस्…",
    filterAllSector:   "सबै क्षेत्र",
    refresh:           "↻ ताजा गर्नुहोस्",
    addBtn:            "+ विशेषज्ञ थप्नुहोस्",
    colPhoto:          "फोटो",
    colName:           "नाम",
    colSector:         "क्षेत्र",
    colEducation:      "शिक्षा",
    colYear:           "उत्कृष्टता वर्ष",
    colActions:        "कार्य",
    loading:           "विशेषज्ञहरू लोड हुँदैछ…",
    empty:             "कुनै विशेषज्ञ भेटिएन।",
    edit:              "सम्पादन",
    delete:            "मेटाउनुहोस्",
    modalAdd:          "➕ विशेषज्ञ थप्नुहोस्",
    modalEdit:         "✏️ विशेषज्ञ सम्पादन गर्नुहोस्",
    lblName:           "नाम *",
    namePlaceholder:   "विशेषज्ञको पूरा नाम",
    lblAddress:        "ठेगाना",
    addressPlaceholder:"ठेगाना",
    lblSector:         "क्षेत्र / विशेषज्ञता *",
    selectSector:      "क्षेत्र छान्नुहोस्",
    lblEducation:      "शिक्षा",
    educationPlaceholder: "शैक्षिक योग्यता",
    lblYear:           "उत्कृष्टता वर्ष",
    yearPlaceholder:   "जस्तै: २०७९",
    lblAdditional:     "थप जानकारी",
    additionalPlaceholder: "थप विवरण, उपलब्धिहरू, आदि...",
    lblPhoto:          "फोटो",
    saveBtnAdd:        "💾 विशेषज्ञ थप्नुहोस्",
    saveBtnEdit:       "💾 परिवर्तन सुरक्षित गर्नुहोस्",
    savingBtn:         "सुरक्षित हुँदैछ…",
    cancel:            "रद्द गर्नुहोस्",
    deleteTitle:       "विशेषज्ञ मेटाउनुहोस्",
    deleteConfirm:     (name) => <>के तपाईं <strong>"{name}"</strong> मेटाउन निश्चित हुनुहुन्छ? यो कार्य पूर्वावस्थामा फर्काउन सकिँदैन।</>,
    deleteYes:         "हो, मेटाउनुहोस्",
    deleting:          "मेटाउँदैछ…",
    errLoad:           "विशेषज्ञहरू लोड गर्न असफल भयो।",
    errSave:           "सुरक्षित गर्न असफल भयो।",
    errDelete:         "मेटाउन असफल भयो।",
  },
  en: {
    statTotal:         "Total Experts",
    statSectors:       "Sectors Covered",
    deltaSoFar:        "So far",
    deltaCovered:      "Covered",
    searchPlaceholder: "🔍 Search by name or education…",
    filterAllSector:   "All Sectors",
    refresh:           "↻ Refresh",
    addBtn:            "+ Add Expert",
    colPhoto:          "Photo",
    colName:           "Name",
    colSector:         "Sector",
    colEducation:      "Education",
    colYear:           "Year of Excellence",
    colActions:        "Actions",
    loading:           "Loading experts…",
    empty:             "No experts found.",
    edit:              "Edit",
    delete:            "Delete",
    modalAdd:          "➕ Add Expert",
    modalEdit:         "✏️ Edit Expert",
    lblName:           "Name *",
    namePlaceholder:   "Expert's full name",
    lblAddress:        "Address",
    addressPlaceholder:"Address",
    lblSector:         "Sector / Speciality *",
    selectSector:      "Select a sector",
    lblEducation:      "Education",
    educationPlaceholder: "Educational qualification",
    lblYear:           "Year of Excellence",
    yearPlaceholder:   "e.g. 2079",
    lblAdditional:     "Additional Information",
    additionalPlaceholder: "Extra details, achievements, etc...",
    lblPhoto:          "Photo",
    saveBtnAdd:        "💾 Add Expert",
    saveBtnEdit:       "💾 Save Changes",
    savingBtn:         "Saving…",
    cancel:            "Cancel",
    deleteTitle:       "Delete Expert",
    deleteConfirm:     (name) => <>Are you sure you want to delete <strong>"{name}"</strong>? This action cannot be undone.</>,
    deleteYes:         "Yes, Delete",
    deleting:          "Deleting…",
    errLoad:           "Failed to load experts.",
    errSave:           "Failed to save.",
    errDelete:         "Failed to delete.",
  },
};

function ExpertsPage() {
  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.en;
  const isMobile = useIsMobile();

  const [sectors, setSectors]           = useState([]); // [{ _id, name, order, ... }] from Sectors API
  const [experts, setExperts]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [search, setSearch]             = useState("");
  const [filterSector, setFilterSector] = useState("all");
  const [modal, setModal]               = useState(null); // "add" | "edit" | "delete"
  const [selected, setSelected]         = useState(null);
  const [form, setForm]                 = useState({ ...EMPTY_FORM });
  const [saving, setSaving]             = useState(false);

  useEffect(() => { fetchExperts(); fetchSectors(); }, []);

  async function fetchSectors() {
    try {
      const res  = await api.get("/sectors");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.sectors ?? []);
      const sorted = [...data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSectors(sorted);
    } catch (e) {
      console.error("Error fetching sectors:", e);
    }
  }

  // NOTE: assumes an admin experts endpoint at /experts (GET all, POST,
  // PUT /:id, DELETE /:id) — adjust the paths below if yours differ.
  async function fetchExperts() {
    setLoading(true); setError("");
    try {
      const res  = await api.get("/experts");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setExperts(data);
    } catch (e) {
      setError(e.response?.data?.message || t.errLoad);
    } finally {
      setLoading(false);
    }
  }

  const filtered = experts.filter(ex => {
    const q = search.toLowerCase();
    const matchSearch = ex.name?.toLowerCase().includes(q) || ex.education?.toLowerCase().includes(q);
    const matchSector = filterSector === "all" || ex.sector === filterSector;
    return matchSearch && matchSector;
  });

  const stats = [
    { label: t.statTotal,   value: experts.length,                                          icon: "🎓", delta: t.deltaSoFar  },
    { label: t.statSectors, value: new Set(experts.map(ex => ex.sector).filter(Boolean)).size, icon: "🏷️", delta: t.deltaCovered },
  ];

  function openAdd()      { setForm({ ...EMPTY_FORM, sector: sectors[0]?.name || "" }); setSelected(null); setModal("add"); }
  function openEdit(ex)   { setForm({ name: ex.name, address: ex.address || "", sector: ex.sector || sectors[0]?.name || "", education: ex.education || "", yearOfExcellence: ex.yearOfExcellence || "", additionalInfo: ex.additionalInfo || "", photo: ex.photoUrl || "" }); setSelected(ex); setModal("edit"); }
  function openDelete(ex) { setSelected(ex); setModal("delete"); }
  function closeModal()   { setModal(null); setSelected(null); setSaving(false); }

  async function handleSave() {
    if (!form.name.trim() || !form.sector) return;
    setSaving(true);
    try {
      const photo   = await uploadImageFile(form.photo);
      const payload = { ...form, photo };
      if (modal === "add") {
        const res     = await api.post("/experts", payload);
        const created = res.data?.data ?? res.data;
        setExperts(prev => [created, ...prev]);
      } else {
        const res     = await api.put(`/experts/${selected._id}`, payload);
        const updated = res.data?.data ?? res.data;
        setExperts(prev => prev.map(ex => ex._id === selected._id ? updated : ex));
      }
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || t.errSave);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await api.delete(`/experts/${selected._id}`);
      setExperts(prev => prev.filter(ex => ex._id !== selected._id));
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || t.errDelete);
    } finally {
      setSaving(false);
    }
  }

  const EXPERTS_COLS = "70px 1.6fr 1.2fr 1.4fr 130px 200px";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(2, 1fr)", gap: 10, maxWidth: isMobile ? "100%" : 460 }}>
        {stats.map(s => (
          <div key={s.label} style={S.statCard}>
            <div style={S.statIcon}>{s.icon}</div>
            <div>
              <div style={S.statVal}>{s.value}</div>
              <div style={S.statLabel}>{s.label}</div>
              <div style={S.statDelta}>{s.delta}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, padding: "12px 16px" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input placeholder={t.searchPlaceholder} value={search} onChange={e => setSearch(e.target.value)} style={{ ...INP, flex: "1 1 200px", width: "auto", minWidth: 0, maxWidth: isMobile ? "100%" : 260 }} />
          <select value={filterSector} onChange={e => setFilterSector(e.target.value)} style={{ ...INP, width: "auto", minWidth: 150, maxWidth: isMobile ? "100%" : 200 }}>
            <option value="all">{t.filterAllSector}</option>
            {sectors.map((s) => (
              <option key={s._id} value={s.name}>{s.name}</option>
            ))}
          </select>
          <button onClick={fetchExperts} style={{ marginLeft: isMobile ? 0 : "auto", padding: "7px 14px", borderRadius: 7, background: G3, border: `1px solid ${G}`, color: G, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t.refresh}</button>
          <button onClick={openAdd} style={{ padding: "7px 16px", borderRadius: 7, background: G, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t.addBtn}</button>
        </div>
      </div>

      {error && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 16px", color: "#ef4444", fontSize: 13 }}>⚠ {error}</div>}

      <div style={{ ...S.tableWrap, ...S.tableScroll }}>
        <div style={{ minWidth: isMobile ? 760 : undefined }}>
          <div style={tblHead(EXPERTS_COLS, isMobile)}>
            <span>{t.colPhoto}</span><span>{t.colName}</span><span>{t.colSector}</span><span>{t.colEducation}</span><span>{t.colYear}</span><span>{t.colActions}</span>
          </div>
          {loading && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.loading}</div>}
          {!loading && filtered.length === 0 && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.empty}</div>}
          {!loading && filtered.map((ex, i) => (
            <TblRow key={ex._id} cols={EXPERTS_COLS} isLast={i === filtered.length - 1}>
              <TblCell fit>
                {ex.photoUrl ? (
                  <img src={getImageUrl(ex.photoUrl)} alt={ex.name}
                    style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", display: "block" }}
                    onError={e => e.target.style.opacity = "0.3"} />
                ) : (
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "#9ca3af" }}>👤</div>
                )}
              </TblCell>
              <TblCell>
                <span style={{ fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", width: "100%" }}>{ex.name}</span>
              </TblCell>
              <TblCell><span style={{ color: "#374151", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", width: "100%" }}>{ex.sector || "—"}</span></TblCell>
              <TblCell><span style={{ color: "#6b7280", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", width: "100%" }}>{ex.education || "—"}</span></TblCell>
              <TblCell fit><span style={{ color: "#6b7280", fontSize: 11, whiteSpace: "nowrap" }}>{ex.yearOfExcellence || "—"}</span></TblCell>
              <TblCell fit>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => openEdit(ex)} style={actBtn("edit")}>{t.edit}</button>
                  <button onClick={() => openDelete(ex)} style={actBtn("delete")}>{t.delete}</button>
                </div>
              </TblCell>
            </TblRow>
          ))}
        </div>
      </div>

      {(modal === "add" || modal === "edit") && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeModal}>
          <div style={{ background: "#fff", borderRadius: 14, width: 560, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", animation: "slideUp 0.22s ease" }} onClick={e => e.stopPropagation()}>
            <div style={{ background: `linear-gradient(135deg, ${G} 0%, #0a5c2e 100%)`, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>{modal === "add" ? t.modalAdd : t.modalEdit}</span>
              <button onClick={closeModal} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 26, height: 26, borderRadius: "50%", cursor: "pointer" }}>×</button>
            </div>
            <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 13 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={LBL}>{t.lblName}</div>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t.namePlaceholder} style={INP} />
                </div>
                <div>
                  <div style={LBL}>{t.lblSector}</div>
                  <select value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })} style={INP}>
                    <option value="" disabled>{t.selectSector}</option>
                    {sectors.map((s) => (
                      <option key={s._id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={LBL}>{t.lblAddress}</div>
                  <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder={t.addressPlaceholder} style={INP} />
                </div>
                <div>
                  <div style={LBL}>{t.lblYear}</div>
                  <input value={form.yearOfExcellence} onChange={e => setForm({ ...form, yearOfExcellence: e.target.value })} placeholder={t.yearPlaceholder} style={INP} />
                </div>
              </div>
              <div>
                <div style={LBL}>{t.lblEducation}</div>
                <input value={form.education} onChange={e => setForm({ ...form, education: e.target.value })} placeholder={t.educationPlaceholder} style={INP} />
              </div>
              <div>
                <div style={LBL}>{t.lblAdditional}</div>
                <textarea value={form.additionalInfo} onChange={e => setForm({ ...form, additionalInfo: e.target.value })} placeholder={t.additionalPlaceholder} style={{ ...INP, minHeight: 72, resize: "vertical" }} />
              </div>
              <div>
                <div style={LBL}>{t.lblPhoto}</div>
                <ImageUploadField value={form.photo} onChange={url => setForm({ ...form, photo: url })} />
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button onClick={handleSave} disabled={saving}
                  style={{ flex: 1, padding: "9px", borderRadius: 8, background: G, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                  {saving ? t.savingBtn : (modal === "add" ? t.saveBtnAdd : t.saveBtnEdit)}
                </button>
                <button onClick={closeModal}
                  style={{ flex: 1, padding: "9px", borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  {t.cancel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modal === "delete" && selected && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeModal}>
          <div style={{ background: "#fff", borderRadius: 14, width: 380, maxWidth: "95vw", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", animation: "slideUp 0.22s ease", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ background: "rgba(239,68,68,0.08)", borderBottom: "1px solid #fee2e2", padding: "16px 20px", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>🗑</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#991b1b" }}>{t.deleteTitle}</span>
            </div>
            <div style={{ padding: "18px 20px" }}>
              <p style={{ fontSize: 13, color: "#374151", margin: 0 }}>
                {t.deleteConfirm(selected.name)}
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button onClick={handleDelete} disabled={saving}
                  style={{ flex: 1, padding: "9px", borderRadius: 8, background: "#ef4444", color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                  {saving ? t.deleting : t.deleteYes}
                </button>
                <button onClick={closeModal}
                  style={{ flex: 1, padding: "9px", borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  {t.cancel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

export default ExpertsPage;
